import { useState, useEffect } from 'react';

// Simple / Advanced view mode for the 3CHA shells.
//
//  - 'simple'   → the nav shows only the essential tabs for that role.
//  - 'advanced' → every tab (the stock behaviour). This is the default,
//                 so nothing changes for anyone until they opt in.
//
// Per-role AND per-device, exactly like the layout-theme picker
// (`3c_owner_layout` / `3c_principal_layout`): owner, principal, and
// detailer each keep their own pick on the same device, and it's
// reversible from the Background → Tools tab (owner/principal) or the
// detailer's settings modal. A same-tab `3c-viewmode-changed` event
// (plus the cross-tab `storage` event) lets any open shell re-render
// live when the toggle flips.

export const VIEWMODE_EVENT = '3c-viewmode-changed';
// Fired when the Simple-view tab SET is customized (distinct from flipping the
// mode itself), so any open shell re-filters its nav live.
export const SIMPLEKEYS_EVENT = '3c-simplekeys-changed';

// DEFAULT tabs that stay visible in Simple mode, per role. The user can override
// this per role from Background → View. Keys match DashNav's NAV / DET_NAV keys.
export const SIMPLE_KEYS = {
  owner:     ['overview', 'schedule', 'requests', 'customers', 'pool', 'invoices', 'comms', 'banner'],
  principal: ['overview', 'schedule', 'requests', 'customers', 'pool', 'invoices', 'comms', 'banner'],
  detailer:  ['checklist', 'tasks', 'pool', 'inbox', 'banner'],
  hd:        ['overview', 'schedule', 'customers', 'tasks', 'comms'],
};

// HARD per-role caps on the nav, independent of Simple/Advanced.
//
// SIMPLE_KEYS is a user preference — the operator can widen it back to
// everything from Background → View. ROLE_KEYS is not: it's the ceiling on what
// a role may reach at all, so Advanced mode can't hand HD the Revenue tab.
//
// `hd` (Ric at Albany) runs the shop floor: overview + weekly/monthly goals,
// schedule, customers, tasks, needs, stock, gift cards, SOPs, comms. Everything
// financial or operator-facing is out — Revenue, Membership, Sales Tax, AI,
// Usage — as are Pool, Reminders and Banner, which simply weren't on the list.
// Those three are harmless; add the key here if Ryder wants them.
//
// This is the UI ceiling; the real boundary is in the database. `hd` is a
// genuine RLS role (migration 20260810140000) with its own policies, scoped to
// its own location — invoices, expenses, members, payroll and marketing return
// zero rows for him no matter what request he makes. So this list is about
// keeping his nav clean, not about keeping him out.
export const ROLE_KEYS = {
  hd: ['overview', 'schedule', 'customers', 'tasks', 'needs', 'stock', 'giftcards', 'comms', 'expenses'],
};

function keyFor(role) { return '3c_view_mode_' + (role || 'principal'); }
function simpleKeysStore(role) { return '3c_simple_keys_' + (role || 'principal'); }

// The customized Simple-view tab set for a role, or the built-in default when the
// user hasn't customized it. Per role + per device (localStorage), like the mode.
export function getSimpleKeys(role) {
  try {
    const raw = localStorage.getItem(simpleKeysStore(role));
    if (raw) { const arr = JSON.parse(raw); if (Array.isArray(arr)) return arr; }
  } catch (_) {}
  return (SIMPLE_KEYS[role] || []).slice();
}

// Save a custom Simple-view tab set (array of nav keys). Passing null/undefined
// clears the override → back to the default. Fires the live-update event.
export function setSimpleKeys(role, keys) {
  try {
    if (Array.isArray(keys)) localStorage.setItem(simpleKeysStore(role), JSON.stringify(keys));
    else localStorage.removeItem(simpleKeysStore(role));
  } catch (_) {}
  try { window.dispatchEvent(new Event(SIMPLEKEYS_EVENT)); } catch (_) {}
}

// True when the role has a saved override (vs the built-in default).
export function hasCustomSimpleKeys(role) {
  try { return !!localStorage.getItem(simpleKeysStore(role)); } catch (_) { return false; }
}

export function getViewMode(role) {
  try { return localStorage.getItem(keyFor(role)) === 'simple' ? 'simple' : 'advanced'; }
  catch (_) { return 'advanced'; }
}

export function setViewMode(role, mode) {
  try {
    if (mode === 'simple') localStorage.setItem(keyFor(role), 'simple');
    else                   localStorage.removeItem(keyFor(role)); // advanced = default = no key
  } catch (_) {}
  // Same-tab signal — open shells listen for this and re-filter the nav
  // without waiting for a storage event (which only fires across tabs).
  try { window.dispatchEvent(new Event(VIEWMODE_EVENT)); } catch (_) {}
}

// Current mode for a role, kept in sync with both the same-tab custom
// event and cross-tab storage changes.
export function useViewMode(role) {
  const [mode, setMode] = useState(() => getViewMode(role));
  useEffect(() => {
    const sync = () => setMode(getViewMode(role));
    sync();
    window.addEventListener(VIEWMODE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(VIEWMODE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, [role]);
  return mode;
}

// The visible-key allowlist for a role when Simple is on, else null
// (meaning "show everything"). Feed straight into DashNav's visibleKeys.
export function visibleKeysForMode(role, mode) {
  const cap = ROLE_KEYS[role] || null;
  if (mode !== 'simple') return cap;                       // null = show everything
  const simple = getSimpleKeys(role);
  return cap ? simple.filter((k) => cap.includes(k)) : simple;
}

// Reactive visible-keys for a shell's nav. Re-renders when the mode flips OR the
// Simple-view tab set is customized (same-tab events) or changed in another tab
// (storage). Use this instead of visibleKeysForMode(role, useViewMode(role)) so
// customizing the Simple tabs updates the nav live.
export function useVisibleKeys(role) {
  const [keys, setKeys] = useState(() => visibleKeysForMode(role, getViewMode(role)));
  useEffect(() => {
    const sync = () => setKeys(visibleKeysForMode(role, getViewMode(role)));
    sync();
    window.addEventListener(VIEWMODE_EVENT, sync);
    window.addEventListener(SIMPLEKEYS_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(VIEWMODE_EVENT, sync);
      window.removeEventListener(SIMPLEKEYS_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, [role]);
  return keys;
}
